// Providers store module for workbench
import { getCustomersByClinicIdApi } from '@/api/setting';
import { notify } from '@/utils/notify';

const state = {
  providers: [],        // All providers (doctors) list
  loading: false,       // Loading state
  error: null          // Error state
};

const getters = {
  // Get all providers
  getAllProviders: (state) => state.providers,
  
  // Get provider by ID
  getProviderById: (state) => (customerId) => {
    return state.providers.find(provider => provider.customer_id === customerId);
  },
  
  // Search providers by name (first name, last name, or full name)
  searchProvidersByName: (state) => (searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return state.providers;
    }
    
    const term = searchTerm.toLowerCase().trim();
    return state.providers.filter(provider => {
      const firstName = (provider.customer_first_name || '').toLowerCase();
      const lastName = (provider.customer_last_name || '').toLowerCase();
      const middleName = (provider.customer_middle_name || '').toLowerCase();
      const fullName = `${firstName} ${middleName} ${lastName}`.trim();
      
      return firstName.includes(term) || 
             lastName.includes(term) || 
             middleName.includes(term) ||
             fullName.includes(term);
    });
  },
  
  // Get loading state
  isLoading: (state) => state.loading,
  
  // Get error state
  getError: (state) => state.error,
  
  // Get provider count
  getProviderCount: (state) => state.providers.length
};

const mutations = {
  SET_PROVIDERS(state, providers) {
    state.providers = providers;
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  
  SET_ERROR(state, error) {
    state.error = error;
  },
  
  CLEAR_PROVIDERS(state) {
    state.providers = [];
    state.error = null;
  }
};

const actions = {
  // Fetch providers from API
  async fetchProviders({ commit }, clinicId) {
    try {
      commit('SET_LOADING', true);
      commit('SET_ERROR', null);
      
      console.log('Fetching providers for clinic:', clinicId);
      
      // Call API to get providers
      const response = await getCustomersByClinicIdApi(clinicId);
      const providersData = response.data.customers || [];
      
      // Filter providers: only keep those with invite_status = 'Account Created' or 'Account Pending'
      const filteredProviders = providersData.filter(provider => {
        const inviteStatus = provider.invite_status;
        return inviteStatus === 'Account Created' || inviteStatus === 'Account Pending';
      });
      
      console.log(`Fetched ${providersData.length} providers, filtered to ${filteredProviders.length}`);
      
      // Store filtered providers
      commit('SET_PROVIDERS', filteredProviders);
      commit('SET_LOADING', false);
      
      return filteredProviders;
    } catch (error) {
      console.error('Failed to fetch providers:', error);
      commit('SET_ERROR', error.response?.data?.message || 'Failed to fetch providers');
      commit('SET_LOADING', false);
      
      notify({
        title: 'Error',
        message: error.response?.data?.message || 'Failed to fetch providers',
        type: 'error',
        duration: 5000
      });
      
      throw error;
    }
  },
  
  // Clear providers
  clearProviders({ commit }) {
    commit('CLEAR_PROVIDERS');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
